import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUrlNumber, useUrlString, useUrlList, useUrlBool, useUrlNumberRange } from '../../hooks/useUrlState';
import { Card, Typography, Tag, Input, Button, Space, Tooltip, Avatar, Select, FilterResetButton, Popover, Popconfirm, theme, message, InputNumber, StatCard, ActionMenu, Modal, Dropdown, MenuItem, MenuLabel, MenuDivider } from '../../ui';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  ArrowLeftRight, ArrowUpDown, Ban, CheckCircle2, Download, Eye, FileDown, Filter, MapPin, Merge, MessageCircle, Pencil, Phone, Plus, Satellite, ShieldCheck, Upload, User, UserX, Users,
} from 'lucide-react';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermissionChecker } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { useServerSort } from '../../hooks/useServerSort';
import { DataTable, DataTableColumn } from '../../components/DataTable';
import { BulkImportModal } from '../../components/BulkImportModal';
import { TemplateButton } from '../../components/TemplateButton';
import { downloadCsv } from '../../lib/downloadCsv';
import { ExportDialog } from '../../components/ExportDialog';
import { RegisteredBy } from '../../components/RegisteredBy';
import { FarmerFormModal } from './FarmerFormModal';
import { FarmerDetailModal } from './FarmerDetailModal';
import { ChangeTerritoryModal } from '../territory/ChangeTerritoryModal';
import { MergeCustomerModal } from '../territory/MergeCustomerModal';
import { TerritoryChips } from '../../components/TerritoryChips';
import { displayMobile, waNumber } from '../../lib/validators';
import { CopyValue } from '../../components/CopyValue';
import { formatIstDate } from '../../lib/time';
import { STATE_NAME } from '../../constants/indianStates';
import { LANGUAGE_NAME } from '../../constants/languages';
import { facetOptions, renderFacetOption, type Facet } from '../../lib/facetOptions';

const STATE_LABEL = STATE_NAME;

// Google Maps link for a farmer: precise pin when GPS exists, else a "village, State" search.
const farmerMapUrl = (r: any): string | null => {
  const lat = Number(r.latitude), lng = Number(r.longitude);
  if (r.latitude != null && r.longitude != null && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  const place = [r.city, r.stateCode ? (STATE_LABEL.get(r.stateCode) ?? r.stateCode) : ''].filter(Boolean).join(', ');
  return place ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}` : null;
};
const LANG_LABEL = LANGUAGE_NAME;

/**
 * What a farmer's status ACTUALLY says.
 *
 * `farmers.status` only ever holds ACTIVE or INACTIVE, so every farmer whose ID document had never
 * been collected still read as a clean green ACTIVE — the register looked fully verified when most
 * of it was not. KYC is now folded into the same chip: an active farmer with no document on file is
 * KYC PENDING. Nothing about BLOCKING changes — only INACTIVE stops a farmer logging in or being
 * visited (`isFarmerBlocked`), exactly as before — this is what the row SAYS, not what it permits.
 */
function farmerStatus(r: any): { label: string; tone: 'green' | 'gold' | 'red' } {
  if (r.status === 'INACTIVE') return { label: 'INACTIVE', tone: 'red' };
  return r.kycDocUrl ? { label: 'ACTIVE', tone: 'green' } : { label: 'KYC PENDING', tone: 'gold' };
}
const opt = facetOptions;
// Language options resolve the raw code (en/mr) to its name via LANG_LABEL, keeping the facet count.
const langOpt = (arr: Facet[]) => facetOptions(arr, (o) => LANG_LABEL[String(o.value)] ?? String(o.value).toUpperCase());

export function FarmersListPage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const [page, setPage] = useUrlNumber('page', 1);
  const [pageSize, setPageSize] = useUrlNumber('pageSize', 20);
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  const [formOpen, setFormOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<any | undefined>();
  const [reassigning, setReassigning] = useState<any | undefined>();
  const [viewing, setViewing] = useState<any | undefined>();

  /*
   * `?focus=<id>` opens that farmer's record.
   *
   * Farmers have no detail ROUTE — the record is a modal on this register — so a link from anywhere
   * else in the app (a visit, a complaint) has to address the row instead. It deliberately does NOT
   * search the loaded page the way `useFocusRow` does: the register is paginated and the farmer being
   * linked to is usually not on page one, which would make the link silently do nothing. The detail
   * modal fetches by id anyway, so handing it the id is enough. The param is consumed once, so
   * closing the record and refreshing does not reopen it.
   */
  const [sp, setSp] = useSearchParams();
  const focusId = sp.get('focus');
  useEffect(() => {
    if (!focusId) return;
    setViewing({ id: focusId });
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('focus');
      return next;
    }, { replace: true });
  }, [focusId, setSp]);
  const hasPerm = usePermissionChecker();
  const canCreate = hasPerm('FARMER.CREATE');
  const canImport = hasPerm('RETAILER.IMPORT');
  // Matches the server's FARMER.EXPORT gate — a VIEW-based check would show a button that 403s.
  const canExport = hasPerm('FARMER.EXPORT');
  const canEdit = hasPerm('FARMER.EDIT');
  const canTransfer = hasPerm('TERRITORY.TRANSFER');
  const canMerge = hasPerm('TERRITORY.SPLIT_MERGE');
  const canStatus = hasPerm('FARMER.STATUS'); // activate/deactivate — Admin / Super Admin only
  const [mergingDup, setMergingDup] = useState<any | undefined>();
  // Activate/deactivate used to be a Popconfirm wrapped around a row button. Now that the action
  // lives in the kebab menu, the confirmation is one dialog keyed to the row that opened it —
  // a Popconfirm anchored to a menu item would be dismissed with the menu itself.
  const [confirmStatus, setConfirmStatus] = useState<any | undefined>();
  const qc = useQueryClient();
  const statusMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      request({ url: `/farmers/${id}/status`, method: 'PATCH', data: { active } }),
    onSuccess: (_d, v) => {
      message.success(v.active ? 'Farmer activated' : 'Farmer deactivated');
      qc.invalidateQueries({ queryKey: ['farmers'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed to change status'),
  });

  // Import owner-assignment: a fallback SP for farmers with no owner (by mobile or territory), plus a
  // one-time "assign unowned by territory" backfill for farmers already imported without an owner.
  const [leftoverSp, setLeftoverSp] = useState<string | undefined>();
  const { data: spUsers } = useQuery({
    queryKey: ['sp-list-all'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON' }),
    enabled: importOpen && canImport,
  });
  const spOptions = ((spUsers?.data ?? []) as any[])
    .filter((u) => u.status === 'active')
    .map((u) => ({ value: String(u.id), label: `${u.fullName}${u.mobile ? ` · ${displayMobile(u.mobile)}` : ''}` }));
  const backfill = useMutation({
    mutationFn: () => request<{ assigned: number; stillUnowned: number }>({ url: '/farmers/backfill-owners', method: 'POST' }),
    onSuccess: (res: any) => {
      message.success(`Assigned ${res?.assigned ?? 0} unowned farmer(s) by territory${res?.stillUnowned ? ` · ${res.stillUnowned} still unassigned` : ''}`);
      qc.invalidateQueries({ queryKey: ['farmers'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Backfill failed'),
  });

  // ---- Filters (server-side) ----
  const [stateF, setStateF] = useUrlList('state');
  const [cropsF, setCropsF] = useUrlList('crops');
  const [viaF, setViaF] = useUrlList('via');
  const [langF, setLangF] = useUrlList('lang');
  const [genderF, setGenderF] = useUrlList('gender');
  const [hasKyc, setHasKyc] = useUrlBool('hasKyc');
  // The toolbar's KYC button filters to farmers that HAVE a document; the KPI tile is about the ones
  // that don't, so it drives its own flag rather than overloading the same one with a tri-state.
  const [kycFilterOn, setKycFilterOn] = useUrlBool('kyc');
  // The GPS and ownership tiles are filters too — a number you cannot click is a dead end when what
  // you actually want is the 311 farmers it just told you about.
  const [gpsOnly, setGpsOnly] = useUrlBool('gps');
  const [unassignedOnly, setUnassignedOnly] = useUrlBool('unassigned');
  const [land, setLand] = useUrlNumberRange('land');
  // Server-side column sort (backend ORDER BY over the full result set, not just this page).
  const { sortState, onSortChange, sort } = useServerSort({ onChange: () => setPage(1) });

  const params = useMemo(() => {
    const p: Record<string, any> = { page, pageSize };
    if (search.trim()) p.search = search.trim();
    if (stateF.length) p.state = stateF.join(',');
    if (cropsF.length) p.crops = cropsF.join(',');
    if (viaF.length) p.registeredVia = viaF.join(',');
    if (langF.length) p.languagePref = langF.join(',');
    if (genderF.length) p.gender = genderF.join(',');
    if (hasKyc) p.hasKyc = 'true';
    if (kycFilterOn) p.hasKyc = 'false';
    if (gpsOnly) p.hasGps = 'true';
    if (unassignedOnly) p.unassigned = 'true';
    if (land) { p.landMin = land[0]; p.landMax = land[1]; }
    if (sort) p.sort = sort;
    return p;
  }, [page, pageSize, search, stateF, cropsF, viaF, langF, genderF, hasKyc, kycFilterOn, gpsOnly, unassignedOnly, land, sort]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['farmers', params],
    queryFn: () => request<any>({ url: '/farmers', params }),
    placeholderData: keepPreviousData,
  });
  const { data: facets } = useQuery({
    queryKey: ['farmers', 'filter-options'],
    queryFn: () => request<any>({ url: '/farmers/filter-options' }),
  });

  const kpis: Record<string, number> = facets?.kpis ?? {};

  const stateOpts = useMemo(() => facetOptions(facets?.states, (s) => String(STATE_LABEL.get(String(s.value)) ?? s.value)), [facets]);
  const landBounds: [number, number] = [Math.floor(facets?.landMin ?? 0), Math.ceil(facets?.landMax ?? 0)];
  const hasLand = landBounds[1] > landBounds[0];

  const activeFilters = stateF.length + cropsF.length + viaF.length + langF.length + genderF.length
    + (hasKyc ? 1 : 0) + (kycFilterOn ? 1 : 0) + (gpsOnly ? 1 : 0) + (unassignedOnly ? 1 : 0) + (land ? 1 : 0) + (search ? 1 : 0);
  const clearFilters = () => {
    setSearchInput(''); setStateF([]); setCropsF([]); setViaF([]); setLangF([]); setGenderF([]);
    setHasKyc(false); setKycFilterOn(false); setGpsOnly(false); setUnassignedOnly(false); setLand(null); setPage(1);
  };

  const columns: DataTableColumn<any>[] = [
    // The column deliberately shows only the first 8 characters, so the value on screen is not the
    // value anyone needs — click-to-copy hands over the whole uuid.
    { key: 'uuid', title: 'ID', dataIndex: 'uuid', width: 110, defaultVisible: false,
      render: (v: string) => v && <CopyValue value={v} label="Farmer ID"><code style={{ fontSize: 11 }}>{v.slice(0, 8)}…</code></CopyValue> },
    { key: 'photoUrl', title: '', dataIndex: 'photoUrl', width: 50, alwaysVisible: true,
      render: (v: string) => <Avatar size={32} src={v} fallback={<User size={16} />} /> },
    {
      key: 'fullName', title: 'Name', dataIndex: 'fullName', sortable: true, alwaysVisible: true, width: 220, ellipsis: true,
      // Signed up in the app and not yet confirmed by their sales person — see People › Farmers to verify.
      render: (v: string, r: any) => (r.verificationStatus === 'UNVERIFIED' ? (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate">{v}</span>
          <Tooltip label={r.routingNote ? `Signed up in the app · ${r.routingNote}` : 'Signed up in the app — not yet verified by their sales person'}>
            <Tag tone="gold">Unverified</Tag>
          </Tooltip>
        </span>
      ) : v),
    },
    {
      key: 'mobile', title: 'Mobile', dataIndex: 'mobile', sortable: true, alwaysVisible: true, width: 140,
      // Click-to-copy, not `tel:` — a drag across the digits pans the table, so the number was
      // unselectable. Calling stays in the row kebab. Same as the retailers register.
      render: (v: string) => v && <CopyValue value={v} label="Mobile">{displayMobile(v)}</CopyValue>,
    },
    { key: 'gender', title: 'Gender', dataIndex: 'gender', width: 90, defaultVisible: false },
    { key: 'languagePref', title: 'Language', dataIndex: 'languagePref', sortable: true, width: 110,
      render: (v: string) => v && <Tag>{LANG_LABEL[v] ?? v.toUpperCase()}</Tag> },
    { key: 'location', title: 'Location', dataIndex: 'city', width: 200, ellipsis: true, alwaysVisible: true,
      render: (city: string, r: any) => {
        const state = r.stateCode ? (STATE_LABEL.get(r.stateCode) ?? r.stateCode) : null;
        const txt = [city, state].filter(Boolean).join(' · ');
        return txt || <span style={{ color: '#bbb' }}>—</span>;
      } },
    /* The same chips the dealers register and the order queues use — State → District → Beat, each
       level in its own colour. A farmer with no territory is not routed to any rep, so that says so
       in amber rather than printing an em-dash the eye skips. */
    { key: 'territory', title: 'Territory', dataIndex: 'territoryId', width: 300,
      render: (_: any, r: any) => <TerritoryChips territoryId={r.territoryId} fallbackName={r.territoryName} /> },
    { key: 'pincode', title: 'Pincode', dataIndex: 'pincode', width: 100 },
    {
      key: 'crops', title: 'Crops', dataIndex: 'crops', ellipsis: true, width: 240,
      render: (crops: string[]) => (crops ?? []).map((c) => <Tag tone="green" key={c}>{c}</Tag>),
    },
    { key: 'landAcres', title: 'Land (ac)', dataIndex: 'landAcres', sortable: true, align: 'right', width: 100,
      render: (v: any) => (v != null ? Number(v) : <span className="text-subtle">—</span>) },
    { key: 'registeredVia', title: 'Source', dataIndex: 'registeredVia', sortable: true, width: 100,
      render: (v: string) => v && <Tag>{v}</Tag> },
    // Status now carries KYC (see farmerStatus) — a separate KYC column would say the same thing twice.
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 130,
      render: (_: string, r: any) => { const st = farmerStatus(r); return <Tag tone={st.tone}>{st.label}</Tag>; } },
    { key: 'assignedSp', title: 'Assigned SP', dataIndex: 'assignedSalesPersonName', width: 170, ellipsis: true,
      render: (v: string) => v || <span className="text-subtle">Unassigned</span> },
    {
      key: 'gps', title: 'GPS', width: 80,
      render: (_: any, r: any) => (r.latitude && r.longitude
        ? <Tag tone="blue">Pinned</Tag>
        : <Tag>None</Tag>),
    },
    { key: 'dob', title: 'Date of birth', dataIndex: 'dob', width: 130, defaultVisible: false,
      render: (v: string) => (v ? formatIstDate(v) : '—') },
    {
      // App access, same pair the 360 page shows: can they sign in, and is a handset already tied to
      // them? One-device-per-user means "bound to an old phone" is a real support case, and spotting
      // it from the register beats opening records one at a time.
      key: 'device', title: 'Device', width: 120,
      render: (_: any, r: any) => (r.deviceBound
        ? <Tooltip label={r.deviceSuffix ? `Bound to a handset ending …${r.deviceSuffix}` : 'A handset is bound'}><span><Tag tone="green">Bound</Tag></span></Tooltip>
        : <Tag>Not bound</Tag>) },
    { key: 'login', title: 'App login', width: 130, defaultVisible: false,
      render: (_: any, r: any) => (r.appUserId
        ? <Tag tone={r.loginStatus === 'active' ? 'green' : 'gold'}>{String(r.loginStatus ?? 'unknown')}</Tag>
        : <Tag>Never logged in</Tag>) },
    { key: 'createdAt', title: 'Registered', dataIndex: 'createdAt', sortable: true, width: 150,
      render: (v: string) => formatIstDate(v) },
    // Who registered the farmer (and in which shift) — the date beside it is WHEN, this is WHO.
    { key: 'registeredBy', title: 'Registered by', width: 190, render: (_: any, r: any) => <RegisteredBy r={r} compact /> },
    {
      /**
       * Two inline actions, everything else behind the kebab.
       *
       * The row carried up to EIGHT 32px icon buttons — view, map, edit, activate, transfer, merge,
       * call, WhatsApp — which is more chrome than data, and the column had to be wide enough for
       * all of them on every row. View and Edit are what people actually reach for; the rest are
       * occasional, and reading them as words in a menu beats guessing eight glyphs.
       */
      key: 'actions', title: '', alwaysVisible: true, width: 140, fixed: 'right',
      render: (_: any, r: any) => {
        const map = farmerMapUrl(r);
        const items = [
          map && { key: 'map', label: 'Location on Google Maps', icon: <MapPin size={16} />, onSelect: () => window.open(map, '_blank', 'noopener') },
          { key: 'call', label: 'Call', icon: <Phone size={16} />, onSelect: () => { window.location.href = `tel:${displayMobile(r.mobile)}`; } },
          { key: 'wa', label: 'WhatsApp', icon: <MessageCircle size={16} />, onSelect: () => window.open(`https://wa.me/${waNumber(r.whatsappNumber ?? r.mobile)}`, '_blank', 'noopener') },
          canTransfer && { key: 'terr', label: 'Change territory', icon: <ArrowLeftRight size={16} />, dividerBefore: true, onSelect: () => setReassigning(r) },
          canMerge && { key: 'merge', label: 'Merge duplicate', icon: <Merge size={16} />, onSelect: () => setMergingDup(r) },
          canStatus && {
            key: 'status',
            label: r.status === 'INACTIVE' ? 'Activate farmer' : 'Deactivate farmer',
            icon: r.status === 'INACTIVE' ? <CheckCircle2 size={16} /> : <Ban size={16} />,
            danger: r.status !== 'INACTIVE',
            dividerBefore: true,
            onSelect: () => setConfirmStatus(r),
          },
        ].filter(Boolean) as any[];
        return (
          <div className="flex w-full items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
            <Tooltip label="View details"><Button size="sm" variant="text" icon={<Eye size={16} />} onClick={() => setViewing(r)} aria-label="View details" /></Tooltip>
            {canEdit && (
              <Tooltip label="Edit"><Button size="sm" variant="text" icon={<Pencil size={16} />} onClick={() => { setEditing(r); setFormOpen(true); }} aria-label="Edit" /></Tooltip>
            )}
            <ActionMenu items={items} label="More actions" />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Farmers / Customers</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Space wrap size="small">
            {canCreate && (
              <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(undefined); setFormOpen(true); }}>
                Register farmer
              </Button>
            )}
            {/* ONE import/export hub, matching the dealer register.
              *
              * These were three separate bordered buttons — Import CSV / Export / Template — which with
              * "Register farmer" and "Assign owners" beside them made five competing controls that ran off
              * the right edge of the screen. They are also not five peers: two are downloads, two are steps
              * of the same bulk import, and only "Assign owners" is an action on the data. Grouped by what
              * they are FOR, with the file format as a detail rather than the label.
              */}
            {(canExport || canImport) && (
              <Dropdown
                align="end"
                panel={
                  <>
                    {canExport && <MenuLabel>Download</MenuLabel>}
                    {canExport && (
                      <MenuItem icon={<Download size={16} />} onClick={() => setExportOpen(true)}>
                        Register — PDF or Excel
                      </MenuItem>
                    )}
                    {canExport && canImport && <MenuDivider />}
                    {canImport && <MenuLabel>Bulk import</MenuLabel>}
                    {canImport && (
                      <MenuItem icon={<Upload size={16} />} onClick={() => setImportOpen(true)}>
                        Upload a filled file — CSV or Excel…
                      </MenuItem>
                    )}
                    {canImport && (
                      <MenuItem
                        icon={<FileDown size={16} />}
                        onClick={() => downloadCsv('/imports/templates/farmers.csv', 'farmers-template.csv')
                          .catch(() => message.error('Template unavailable'))}
                      >
                        Blank template — CSV
                      </MenuItem>
                    )}
                  </>
                }
              >
                {/* Two-way hub, so a lone download arrow would announce only half of it. */}
                <Button size="sm" icon={<ArrowUpDown size={16} />}>Import / export</Button>
              </Dropdown>
            )}
            {canImport && (
              <Popconfirm
                title="Assign unowned farmers?"
                description="Give every farmer that has no sales person the primary SP of its territory."
                okText="Assign" onConfirm={() => backfill.mutate()}
              >
                <Button size="sm" icon={<ShieldCheck size={16} />} loading={backfill.isPending}>Assign owners</Button>
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>
      {/* Headline numbers. These come from the server over the WHOLE scoped register, never from the
          rows on screen — a tile counted off the current page reports a different total depending on
          where you are in the table. The KYC tile doubles as the filter for the work it names. */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Farmers" tone="brand" icon={<Users className="size-4" />}
          value={(kpis.total ?? 0).toLocaleString('en-IN')}
          hint={`${(kpis.active ?? 0).toLocaleString('en-IN')} active · ${(kpis.inactive ?? 0).toLocaleString('en-IN')} inactive`}
          active={activeFilters === 0}
          onClick={clearFilters}
        />
        <StatCard
          label="KYC pending" tone="gold" icon={<ShieldCheck className="size-4" />}
          value={(kpis.kycPending ?? 0).toLocaleString('en-IN')}
          hint={kpis.total ? `${Math.round(((kpis.kycPending ?? 0) / kpis.total) * 100)}% of the register` : 'No ID document on file'}
          active={hasKyc === false && kycFilterOn}
          onClick={() => { const on = !kycFilterOn; setKycFilterOn(on); setHasKyc(false); if (on) { setGpsOnly(false); setUnassignedOnly(false); } setPage(1); }}
        />
        <StatCard
          label="GPS pinned" tone="sky" icon={<Satellite className="size-4" />}
          value={(kpis.withGps ?? 0).toLocaleString('en-IN')}
          hint={`${Math.max(0, (kpis.total ?? 0) - (kpis.withGps ?? 0)).toLocaleString('en-IN')} without a location`}
          active={gpsOnly}
          onClick={() => { const on = !gpsOnly; setGpsOnly(on); if (on) { setKycFilterOn(false); setUnassignedOnly(false); } setPage(1); }}
        />
        <StatCard
          label="Unassigned" tone={kpis.unassigned ? 'danger' : 'neutral'} icon={<UserX className="size-4" />}
          value={(kpis.unassigned ?? 0).toLocaleString('en-IN')}
          hint="No sales person owns them"
          active={unassignedOnly}
          onClick={() => { const on = !unassignedOnly; setUnassignedOnly(on); if (on) { setKycFilterOn(false); setGpsOnly(false); } setPage(1); }}
        />
      </div>
      <Card>
        <Space wrap size={[8, 8]} style={{ marginBottom: 12, width: '100%' }}>
          <Input
            placeholder="Search name, mobile, WhatsApp" aria-label="Search name, mobile, WhatsApp" prefix={<Filter size={16} style={{ color: token.colorTextTertiary }} />}
            className="w-80" value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
          />
          <Select mode="multiple" allowClear placeholder="Gender" style={{ minWidth: 120 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={genderF} onChange={(v) => { setGenderF(v); setPage(1); }} options={opt(facets?.genders)} />
          <Select mode="multiple" allowClear placeholder="Language" style={{ minWidth: 130 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={langF} onChange={(v) => { setLangF(v); setPage(1); }} options={langOpt(facets?.languages)} />
          <Select mode="multiple" allowClear showSearch placeholder="State" style={{ minWidth: 150 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={stateF} onChange={(v) => { setStateF(v); setPage(1); }} options={stateOpts} />
          <Select mode="multiple" allowClear showSearch placeholder="Crops" style={{ minWidth: 160 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={cropsF} onChange={(v) => { setCropsF(v); setPage(1); }} options={opt(facets?.crops)} />
          {hasLand && (
            <Popover trigger="click" title="Land (acres)" content={
              /* Typed numbers, not a slider. A two-handle slider over a range this wide showed no figures at
                 all — you could not read what you had picked, let alone land on "5 to 10". The register's own
                 min/max are the placeholders, so the bounds stay visible without pretending to be the value. */
              <div style={{ width: 232 }}>
                <div className="flex items-center gap-2">
                  <InputNumber
                    aria-label="Least land, in acres" size="small" min={landBounds[0]} max={land?.[1] ?? landBounds[1]}
                    placeholder={String(landBounds[0])} value={land?.[0] ?? null}
                    onChange={(v) => { setLand([v ?? landBounds[0], land?.[1] ?? landBounds[1]]); setPage(1); }}
                  />
                  <span className="type-caption text-subtle">to</span>
                  <InputNumber
                    aria-label="Most land, in acres" size="small" min={land?.[0] ?? landBounds[0]} max={landBounds[1]}
                    placeholder={String(landBounds[1])} value={land?.[1] ?? null}
                    onChange={(v) => { setLand([land?.[0] ?? landBounds[0], v ?? landBounds[1]]); setPage(1); }}
                  />
                </div>
                <div className="type-caption text-subtle" style={{ marginTop: 6 }}>
                  Register holds {landBounds[0]}–{landBounds[1]} acres
                </div>
                {land && <Button size="sm" variant="text" style={{ padding: 0, marginTop: 4 }} onClick={() => { setLand(null); setPage(1); }}>Reset</Button>}
              </div>
            }>
              <Button>{land ? `${land[0]}–${land[1]} ac` : 'Land'}</Button>
            </Popover>
          )}
          <Select mode="multiple" allowClear placeholder="Source" style={{ minWidth: 130 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={viaF} onChange={(v) => { setViaF(v); setPage(1); }} options={opt(facets?.registeredVia)} />
          <Tooltip label="Has KYC document">
            <Button icon={<ShieldCheck size={16} />} variant={hasKyc ? 'primary' : 'default'} onClick={() => { setHasKyc((v) => !v); setPage(1); }}>KYC</Button>
          </Tooltip>
          {activeFilters > 0 && (
            <FilterResetButton size="md" count={activeFilters} onClick={clearFilters} />
          )}
        </Space>

        <DataTable
          storageKey="farmers-list"
          rowKey="id"
          dataSource={data?.data ?? []}
          loading={isLoading || isFetching}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          // The whole row opens the farmer, matching Retailers and Staffs — the eye icon was the only
          // way in, which meant hitting a 32px target to do the most common thing on the page.
          // The action cluster stops propagation, so its buttons still do their own jobs.
          onRow={(r: any) => ({ onClick: () => setViewing(r), style: { cursor: 'pointer' } })}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total: data?.meta?.total ?? 0,
            showSizeChanger: true,
            showTotal: (t) => `${t.toLocaleString('en-IN')} farmers`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>
      <FarmerFormModal open={formOpen} record={editing} onClose={() => { setFormOpen(false); setEditing(undefined); }} />
      <FarmerDetailModal
        farmer={viewing}
        open={!!viewing}
        onClose={() => setViewing(undefined)}
        // Hand off to the edit form and close the detail: stacking one dialog on another buries the
        // first behind a second backdrop and leaves two Escape targets.
        onEdit={canEdit ? () => { setEditing(viewing); setFormOpen(true); setViewing(undefined); } : undefined}
      />
      <Modal
        open={!!confirmStatus}
        onOpenChange={(o) => { if (!o) setConfirmStatus(undefined); }}
        title={confirmStatus?.status === 'INACTIVE' ? 'Activate this farmer?' : 'Deactivate this farmer?'}
        footer={
          <>
            <Button variant="default" onClick={() => setConfirmStatus(undefined)}>Cancel</Button>
            <Button
              variant={confirmStatus?.status === 'INACTIVE' ? 'primary' : 'danger'}
              loading={statusMut.isPending}
              onClick={() => {
                statusMut.mutate({ id: String(confirmStatus.id), active: confirmStatus.status === 'INACTIVE' });
                setConfirmStatus(undefined);
              }}
            >
              {confirmStatus?.status === 'INACTIVE' ? 'Activate' : 'Deactivate'}
            </Button>
          </>
        }
      >
        <p className="type-body text-muted">
          <b className="text-content">{confirmStatus?.fullName}</b>{' '}
          {confirmStatus?.status === 'INACTIVE'
            ? 'will be marked ACTIVE and can use the app again.'
            : 'will be marked INACTIVE — they cannot log in or be visited until reactivated.'}
        </p>
      </Modal>
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export farmer register"
        description="Only the farmers in your scope, as PDF or Excel."
        baseUrl="/farmers/export"
        hidePeriod
        /* EVERY filter the page is showing. This dialog used to send nothing at all, so narrowing the
           register to (say) Maharashtra farmers with no KYC and pressing Export handed back every
           farmer in scope under a heading that called itself the filtered register. `page`/`pageSize`
           are dropped: the export is the whole matching set, not the page being looked at. */
        baseParams={(() => { const p = { ...params }; delete p.page; delete p.pageSize; return p; })()}
      />
      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['farmers'] })}
        kind="farmers"
        entityPlural="farmers"
        allowUpsert
        columns="fullName, mobile, gender, dob, languagePref, territoryCode, salesPersonMobile, city, address, stateCode, pincode, latitude, longitude, crops, landAcres"
        note={<>Required: <b>fullName</b> and <b>mobile</b> (unique). Everything else is optional. <b>salesPersonMobile</b> = the sales person’s login mobile (blank → auto-assign by territory); <b>territoryCode</b> = a territory’s code (e.g. MH-JALGAON); <b>stateCode</b> = 2-digit GST code (e.g. 27); <b>dob</b> = YYYY-MM-DD; <b>crops</b> = separate with “;”. </>}
        extraControl={
          <div>
            <div style={{ fontSize: 12.5, marginBottom: 4, color: token.colorTextSecondary }}>
              Assign farmers with no sales person (by mobile or territory) to:
            </div>
            <Select
              allowClear showSearch optionFilterProp="label" className="w-full"
              placeholder="Optional — leave blank to import them unassigned"
              value={leftoverSp} onChange={setLeftoverSp} options={spOptions}
            />
          </div>
        }
        extraParams={() => (leftoverSp ? { leftoverSalesPersonId: leftoverSp } : {})}
      />
      {canTransfer && (
        <ChangeTerritoryModal open={!!reassigning} onClose={() => setReassigning(undefined)} customerType="FARMER" customer={reassigning ?? {}} />
      )}
      {canMerge && (
        <MergeCustomerModal open={!!mergingDup} onClose={() => setMergingDup(undefined)} customerType="FARMER" duplicate={mergingDup ?? {}} />
      )}
    </div>
  );
}
